import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { fetchMarkets } from '../../api'

function formatPrice(price) {
  if (price >= 1000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
}

function formatVolume(vol) {
  if (vol >= 1e9) return `$${(vol / 1e9).toFixed(1)}B`
  if (vol >= 1e6) return `$${(vol / 1e6).toFixed(0)}M`
  return `$${(vol / 1e3).toFixed(0)}K`
}

function getTopByActivity(data) {
  if (!data) return []

  const items = []

  // Crypto: use actual 24h volume
  if (data.crypto?.coins) {
    for (const coin of data.crypto.coins) {
      items.push({
        symbol: coin.symbol,
        name: coin.name,
        price: coin.price,
        change_pct: coin.change_24h_pct,
        volume: coin.volume_24h,
        type: 'crypto'
      })
    }
  }

  // Equities: use absolute % change as activity proxy
  if (data.equities?.assets) {
    for (const eq of data.equities.assets) {
      items.push({
        symbol: eq.symbol,
        name: eq.name,
        price: eq.price,
        change_pct: eq.change_pct,
        volume: Math.abs(eq.change_pct) * 1e9,
        type: 'equity'
      })
    }
  }

  // Commodities: same approach
  if (data.commodities?.assets) {
    for (const cm of data.commodities.assets) {
      items.push({
        symbol: cm.symbol,
        name: cm.name,
        price: cm.price,
        change_pct: cm.change_pct,
        volume: Math.abs(cm.change_pct) * 1e8,
        type: 'commodity'
      })
    }
  }

  // Sort by volume descending, take top 5
  items.sort((a, b) => b.volume - a.volume)
  return items.slice(0, 5)
}

export default function MarketTicker({ autoRefresh }) {
  const { data } = useQuery({
    queryKey: ['markets'],
    queryFn: fetchMarkets,
    refetchInterval: autoRefresh ? 5 * 60 * 1000 : false,
    retry: 1,
  })

  const topMarkets = getTopByActivity(data)
  if (topMarkets.length === 0) return null

  return (
    <div className="bg-gray-900/60 border-y border-emerald-500/10 py-2.5 md:py-2.5 mb-4 md:mb-6 -mx-3 md:mx-0 md:rounded-lg animate-fade-in">
      <div className="flex items-center justify-center gap-3 md:gap-5 px-3 md:px-4 flex-wrap">
        {topMarkets.map((item, i) => {
          const isUp = item.change_pct >= 0
          return (
            <div key={i} className="flex items-center gap-1.5 md:gap-2 whitespace-nowrap">
              <span className="text-[11px] md:text-xs font-black tracking-wide text-white">
                {item.symbol}
              </span>
              <span className="text-[12px] md:text-sm font-semibold text-gray-300">
                ${formatPrice(item.price)}
              </span>
              <span className={`flex items-center gap-0.5 text-[11px] md:text-xs font-bold ${
                isUp ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {isUp ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {isUp ? '+' : ''}{item.change_pct.toFixed(2)}%
              </span>
              {item.type === 'crypto' && (
                <span className="text-[9px] text-gray-600 font-medium hidden sm:inline">
                  Vol {formatVolume(item.volume)}
                </span>
              )}
              {i < topMarkets.length - 1 && (
                <span className="w-px h-3.5 bg-white/10 ml-1.5 md:ml-2" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
